function debounce(fn, wait) {
	let t;
	return (...args) => {
		clearTimeout(t);
		t = setTimeout(() => fn.apply(this, args), wait);
	};
}

/**
 * Global Cart Utilities
 * Reusable methods for cart operations across the codebase
 */
window.CartUtils = {
	/**
	 * Sections to update when cart changes
	 */
	sections: ['cart-drawer', 'cart-bubble'],

	/**
	 * Add item to cart and update UI
	 * @param {number} variantId - The variant ID to add
	 * @param {number} quantity - Quantity to add (default: 1)
	 * @param {Object} properties - Optional line item properties
	 * @returns {Promise<Object>} - The cart add response
	 */
	async addToCart(variantId, quantity = 1, properties = {}) {
		const formData = {
			items: [
				{
					id: variantId,
					quantity: quantity,
					properties: properties,
				},
			],
			sections: this.sections.join(','),
			sections_url: window.location.pathname,
		};

		const response = await fetch('/cart/add.js', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Accept: 'application/json',
			},
			body: JSON.stringify(formData),
		});

		const data = await response.json();

		if (data.status) {
			throw new Error(data.description || 'Failed to add to cart');
		}

		if (data.sections) {
			this.updateSections(data.sections);
		}

		this.dispatchCartUpdated();
		return data;
	},

	/**
	 * Update cart sections from sections response
	 * @param {Object} sections - The sections object from Shopify response
	 */
	updateSections(sections) {
		// Update cart drawer content
		const cartDrawerSection = sections['cart-drawer'];
		if (cartDrawerSection) {
			const parser = new DOMParser();
			const doc = parser.parseFromString(cartDrawerSection, 'text/html');

			// Update side panel content
			const cartDrawer = document.querySelector('.cart-drawer .side-panel-content');
			if (cartDrawer) {
				const newContent = doc.querySelector('.side-panel-content');
				if (newContent) {
					cartDrawer.innerHTML = newContent.innerHTML;
				}
			}

			// Update side panel footer (totals); hide when cart empty (Liquid omits footer when cart.items.size == 0)
			const cartDrawerFooter = document.querySelector('.cart-drawer .side-panel-footer');
			const newFooter = doc.querySelector('.side-panel-footer');
			const inner = document.querySelector('.cart-drawer .side-panel-inner');
			if (newFooter) {
				if (cartDrawerFooter) {
					cartDrawerFooter.innerHTML = newFooter.innerHTML;
					cartDrawerFooter.style.display = '';
					cartDrawerFooter.removeAttribute('hidden');
				} else if (inner) {
					inner.appendChild(newFooter.cloneNode(true));
				}
			} else if (cartDrawerFooter) {
				cartDrawerFooter.style.display = 'none';
				cartDrawerFooter.setAttribute('hidden', '');
			}

			// Re-initialize quantity inputs
			const quantities = document.querySelectorAll('.cart-drawer .quantity:not(.buttons_added)');
			quantities.forEach((quantity) => {
				new QuantityInput(quantity);
			});
		}

		// Update all cart bubble counts
		const cartBubbleSection = sections['cart-bubble'];
		if (cartBubbleSection) {
			const parser = new DOMParser();
			const doc = parser.parseFromString(cartBubbleSection, 'text/html');
			const newCount = doc.querySelector('.thb-item-count');
			if (newCount) {
				document.querySelectorAll('.thb-item-count').forEach((count) => {
					count.innerHTML = newCount.innerHTML;
				});
			}
		}
	},

	/**
	 * Open the cart drawer
	 */
	openCartDrawer() {
		document.body.classList.add('open-cc');
		if (window.CartDrawer) {
			window.CartDrawer.container.classList.add('active');
			window.CartDrawer.container.focus();
		}
	},

	/**
	 * Dispatch cart updated event
	 */
	dispatchCartUpdated() {
		if (typeof window.getCartData === 'function') {
			window.getCartData();
		}
	},

	/**
	 * Subscribe to cart updated events
	 * @param {Function} callback - Function to call when cart is updated
	 * @returns {Function} - Unsubscribe function
	 */
	onCartUpdated(callback) {
		const handler = () => callback();
		document.addEventListener('cart:updated', handler);
		return () => document.removeEventListener('cart:updated', handler);
	},
};

// Track initialized quantity inputs to prevent duplicates
const initializedQuantityInputs = new WeakSet();

/**
 *  @class
 *  @function Quantity
 */
class QuantityInput {
	constructor(self, decreaseText, increaseText) {
		// Prevent duplicate initialization using WeakSet
		if (initializedQuantityInputs.has(self)) {
			return;
		}

		this.input = self.querySelector('input[name="quantity"]') ? self.querySelector('input[name="quantity"]') : self.querySelector('.qty');
		this.changeEvent = new Event('change', {
			bubbles: true,
		});
		// Create buttons
		this.subtract = self.querySelector('.minus');
		this.add = self.querySelector('.plus');

		// Add functionality to buttons
		this.subtract && this.subtract.addEventListener('click', () => this.change_quantity(-1));
		this.add && this.add.addEventListener('click', (e) => {
			this.change_quantity(1);
			// POPULATE DATALAYER
		});

		self.classList.add('buttons_added');
		initializedQuantityInputs.add(self);
	}

	change_quantity(change, self) {
		// Get current value
		let quantity = Number(this.input.value);

		// Ensure quantity is a valid number
		if (isNaN(quantity)) quantity = 1;

		// Change quantity
		quantity += change;

		// Ensure quantity is always a number
		quantity = Math.max(quantity, 1);

		// Output number
		this.input.value = quantity;

		this.input.dispatchEvent(this.changeEvent);
	}
}
let quantities = document.querySelectorAll('.quantity:not(.buttons_added):not(.dynamic-product-form__quantity)');

quantities.forEach((quantity) => {
	new QuantityInput(quantity);
});

/**
 *  @class
 *  @function ArrowSubMenu
 */
class ArrowSubMenu {
	constructor(self) {
		this.submenu = self.parentNode.querySelector('.sub-menu');
		this.arrow = self;
		// Add functionality to buttons
		self.addEventListener('click', (e) => this.toggle_submenu(e));
	}

	toggle_submenu(e) {
		e.preventDefault();
		let submenu = this.submenu;

		if (!submenu.classList.contains('active')) {
			submenu.classList.add('active');
		} else {
			submenu.classList.remove('active');
			this.arrow.blur();
		}
	}
}
let arrows = document.querySelectorAll('.thb-arrow');
arrows.forEach((arrow) => {
	new ArrowSubMenu(arrow);
});


class ProductGrid {
	constructor(self) {
		this.swatches = self.querySelector('.product-grid-swatches');
		this.image = self.querySelector('.product-primary-image');
		this.stars = self.querySelector('.product-grid__reviews');
		this.handle = self.dataset.handle;

		if (this.swatches) {
			this.enableSwatches(this.swatches, this.image);
		}


		this.checkImage();
		this.checkReviews();
	}

	enableSwatches(swatches, image) {
		let swatch_list = swatches.querySelectorAll('.product-card-swatch'),
			active = swatches.querySelector('.product-card-swatch.active');

		swatch_list.forEach((swatch, index) => {
			swatch.addEventListener('mouseover', function () {
				[].forEach.call(swatch_list, function (el) {
					el.classList.remove('active');
				});
				if (image) {
					image.setAttribute('srcset', swatch.dataset.srcset);
				}

				swatch.classList.add('active');
			});
			swatch.addEventListener('click', function (evt) {
				window.location.href = this.dataset.href;
				evt.preventDefault();
			});
		});
	}


	checkImage() {
		const addClass = () => {
			const orientation = this.image.naturalWidth >= this.image.naturalHeight ? 'image--horizontal' : 'image--vertical';
			this.image.classList.add(orientation);
		};

		if (!this.image.classList.contains('image--vertical') && !this.image.classList.contains('image--horizontal')) {
			if (this.image.naturalWidth === 0 || this.image.naturalHeight === 0) {
				this.image.onload = () => addClass();
			} else {
				addClass();
			}
		}
	}

	checkReviews() {
		const markupReview = (rating, number) => {
			const ratingNew = parseFloat(rating);

			return `
				<div class="reviews__stars flex-container flex-fir-row">
					${[1, 2, 3, 4, 5]
					.map((num) => {
						if (num <= ratingNew) {
							return `<i class="star">
								<svg width="28" height="27" viewBox="0 0 28 27" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M13.9997 21.825L6.12925 26.2305C5.75695 26.4389 5.31148 26.1153 5.39461 25.6968L7.15215 16.85L0.529301 10.726C0.216035 10.4363 0.386174 9.91258 0.809872 9.86233L9.76715 8.8L13.5456 0.609201C13.7243 0.221792 14.275 0.221791 14.4537 0.609201L18.2321 8.8L27.1894 9.86233C27.6131 9.91258 27.7833 10.4363 27.47 10.726L20.8472 16.85L22.6047 25.6968C22.6878 26.1153 22.2423 26.4389 21.87 26.2305L13.9997 21.825Z" fill="#52B480"></path>
								</svg>
							</i>`;
						} else if (ratingNew % 1 >= 0.4 && ratingNew % 1 <= 0.6) {
							return `<i class="star">
								<svg width="28" height="27" viewBox="0 0 28 27" fill="none" xmlns="http://www.w3.org/2000/svg">
									<g>
										<path fill="#52B480" style="transform:scale(0.77) rotateY(180deg) translate(-130%, 0px);" d="M36.041,13.907c-0.123-0.377-0.456-0.646-0.85-0.687l-11.549-1.172L18.96,1.431c-0.16-0.364-0.519-0.598-0.915-0.598
										s-0.755,0.234-0.915,0.598l-4.683,10.618L0.899,13.221c-0.394,0.04-0.728,0.31-0.85,0.688c-0.123,0.377-0.011,0.791,0.285,1.055
										l8.653,7.736L6.534,34.044c-0.083,0.387,0.069,0.787,0.39,1.021c0.322,0.231,0.75,0.254,1.091,0.056l10.031-5.839l10.031,5.839
										c0.156,0.091,0.33,0.136,0.503,0.136c0.207,0,0.413-0.064,0.588-0.191c0.32-0.23,0.474-0.633,0.391-1.02L27.105,22.7l8.652-7.737
										C36.052,14.698,36.164,14.284,36.041,13.907z M8.968,32.249l2.097-9.696c0.076-0.352-0.043-0.717-0.311-0.956l-7.396-6.613
										l9.871-1.002c0.358-0.035,0.669-0.263,0.814-0.592l3.503-7.943v21.812c-0.001,0-0.002,0-0.003,0.001L8.968,32.249z"/>
									</g>
								</svg>
							</i>`;
						} else if (Math.ceil(ratingNew) === num) {
							return `<i class="star">
								<svg width="28" height="27" viewBox="0 0 28 27" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M13.9997 21.825L6.12925 26.2305C5.75695 26.4389 5.31148 26.1153 5.39461 25.6968L7.15215 16.85L0.529301 10.726C0.216035 10.4363 0.386174 9.91258 0.809872 9.86233L9.76715 8.8L13.5456 0.609201C13.7243 0.221792 14.275 0.221791 14.4537 0.609201L18.2321 8.8L27.1894 9.86233C27.6131 9.91258 27.7833 10.4363 27.47 10.726L20.8472 16.85L22.6047 25.6968C22.6878 26.1153 22.2423 26.4389 21.87 26.2305L13.9997 21.825Z" fill="#52B480"></path>
								</svg>
							</i>`;
						} else {
							return `<i class="star">
								<svg width="28" height="27" viewBox="0 0 28 27" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M14.4885 20.9524L14 20.679L13.5116 20.9524L6.58703 24.8285L8.13335 17.0449L8.24242 16.4958L7.83143 16.1158L2.00449 10.7277L9.88529 9.79304L10.4411 9.72712L10.6756 9.21888L14 2.01229L17.3245 9.21888L17.5589 9.72712L18.1147 9.79304L25.9955 10.7277L20.1686 16.1158L19.7576 16.4958L19.8667 17.0449L21.413 24.8285L14.4885 20.9524Z" stroke="#52B480" stroke-width="2"/>
								</svg>
							</i>`;
						}
					})
					.join('')}
					<span class="reviews__rating">${rating}</span>
				</div>
				<a href="/pages/customer-reviews" title="Rating" class="reviews__link">${number} reviews</a>`;
		};

		if (this.stars && !this.stars.querySelector('.reviews__rating') && Object.keys(window.store.reviewsData).length != 0) {
			if (window.store.reviewsData[this.handle].rating != '' && window.store.reviewsData[this.handle].number != '') {
				this.stars.innerHTML = markupReview(window.store.reviewsData[this.handle].rating, window.store.reviewsData[this.handle].number);
			}
		}
	}
}

let products = document.querySelectorAll('.product-grid');
products.forEach((product) => {
	new ProductGrid(product);
});

/**
 *  @class
 *  @function MobileMenu
 */
class MobileMenu {
	constructor() {
		const header = document.getElementById('shopify-section-header'),
			menu = document.getElementById('mobile-menu'),
			links = menu.querySelectorAll('.menu-item-has-children'),
			toggle = header.querySelector('.mobile-toggle-wrapper'),
			setAnnouncementHeight = this.setAnnouncementHeight;

		links.forEach((link) => {
			let link_container = link.querySelector(':scope > .link-container');
			let intelligems_menu_entry = link.classList.contains('menu-item--prevent-open-inteligems');

			// Skip if link-container doesn't exist
			if (!link_container) {
				//console.warn('link-container not found for:', link);
				return;
			}

			let link_a = link_container.querySelector('.link') || link_container.querySelector('a'),
				link_span = link_container.querySelector(':scope > span');

			// Only add event listeners if elements exist
			if (link_span) {
				link_span.addEventListener('click', (e) => {
					link.classList.toggle('active');

					e.preventDefault();
				});
			}

			if (link_a) {
				link_a.addEventListener('click', (e) => {
					intelligems_menu_entry = link.classList.contains('menu-item--prevent-open-inteligems');
					if (intelligems_menu_entry !== true) {
						link.classList.toggle('active');

						e.preventDefault();
					} else {
						window.location.href = link_a.href;
					}

					if (!link.classList.contains('active')) {
						link_a.blur();

						e.preventDefault();
					}
				});
			}
		});
		let closeAnimation = this.closeAnimation;

		document.addEventListener('keydown', (e) => {
			if (e.code && e.code.toUpperCase() === 'ESCAPE') {
				toggle.removeAttribute('open');
				toggle.classList.remove('active');
				document.body.classList.remove('mobile-menu-open');
			}
		});
		toggle.querySelector('.mobile-toggle').addEventListener('click', (e) => {
			e.stopPropagation();
			toggle.classList.toggle('active');
			document.body.classList.toggle('mobile-menu-open', toggle.classList.contains('active'));
			setAnnouncementHeight(header);
			if (!toggle.classList.contains('active')) {
				this.closeAnimation(toggle);
				document.body.classList.remove('mobile-menu-open');
			}
			window.dispatchEvent(new Event('resize.resize-select'));
		});

		document.addEventListener('click', (e) => {
			if (!toggle.classList.contains('active')) {
				return;
			}

			const clickedOnToggleButton = e.target.closest('.mobile-toggle');
			const clickedInsideMenuList = e.target.closest('.mobile-menu');

			if (!clickedOnToggleButton && !clickedInsideMenuList) {
				toggle.classList.remove('active');
				document.body.classList.remove('mobile-menu-open');
				setAnnouncementHeight(header);
				this.closeAnimation(toggle);
				window.dispatchEvent(new Event('resize.resize-select'));
			}
		});

		// Mobile Menu offset
		window.addEventListener('scroll', function () {
			if (window.innerWidth < 1069) {
				setAnnouncementHeight(header);
			}
		});
		window.dispatchEvent(new Event('scroll'));
	}
	setAnnouncementHeight(header) {
		let h = header.getBoundingClientRect().top;
		document.documentElement.style.setProperty('--header-offset', h + 'px');
	}
	closeAnimation(detailsElement) {
		let animationStart;

		const handleAnimation = (time) => {
			if (animationStart === undefined) {
				animationStart = time;
			}

			const elapsedTime = time - animationStart;

			if (elapsedTime < 400) {
				window.requestAnimationFrame(handleAnimation);
			} else {
				detailsElement.removeAttribute('open');
			}
		};

		window.requestAnimationFrame(handleAnimation);
	}
}
/**
 *  @class
 *  @function PanelClose
 */
class PanelClose extends HTMLElement {
	constructor() {
		super();
		let cc = document.querySelector('.click-capture');

		// Add functionality to buttons
		this.addEventListener('click', (e) => this.close_panel(e));
		document.addEventListener('keydown', (e) => {
			if (e.code && e.code.toUpperCase() === 'ESCAPE') {
				document.querySelectorAll('.side-panel').forEach((panel) => {
					panel.classList.remove('active');
					document.body.classList.remove('open-cc');
				});
			}
		});

		cc?.addEventListener('click', (e) => {
			document.body.classList.remove('open-cc');
			if (document.querySelector('.side-panel.active')) {
				document.querySelector('.side-panel.active').classList.remove('active');
			}

			if (document.querySelector('facetfiltersformmobile.active')) {
				document.querySelector('facetfiltersformmobile.active').classList.remove('active');
			}
		});
	}

	close_panel(e) {
		e.preventDefault();
		let panel = e.target.closest('.side-panel');
		panel.classList.remove('active');
		document.body.classList.remove('open-cc');
	}
}
customElements.define('side-panel-close', PanelClose);

/**
 *  @class
 *  @function CartDrawer
 */
class CartDrawer {
	constructor() {
		// Prevent multiple instances
		if (window.CartDrawer) {
			return window.CartDrawer;
		}

		this.container = document.getElementById('Cart-Drawer');
		// Track the current fetch so that rapid changes can cancel older ones
		this._quantityUpdateController = null;
		// Tracker for the most recent cart quantity update request.
		this._latestQuantityRequestId = 0;
		let _this = this,
			buttons = document.querySelectorAll('.thb-cart-drawer-toggle'),
			quantities = this.container.querySelectorAll('.quantity input'),
			removes = this.container.querySelectorAll('.remove');

		// Add functionality to buttons
		buttons.forEach((el) =>
			el.addEventListener('click', (e) => {
				e.preventDefault();

				if (window.location.pathname === '/cart') return;

				document.body.classList.add('open-cc');
				this.container.classList.add('active');
				this.container.focus();
			})
		);

		this.debouncedOnChange = debounce((event) => {
			this.onChange(event);
		}, 1000);

		this.container.addEventListener('change', this.debouncedOnChange.bind(this));

		// Set up event delegation for remove buttons
		this.container.addEventListener('click', (event) => {
			const removeButton = event.target.closest('.remove');
			if (removeButton) {
				event.preventDefault();
				const index = removeButton.dataset.index;
				if (index) {
					this.updateQuantity(index, '0');
				}
			}
		});

		this.notesToggle();
		this.updateCartUI = this.updateCartUI.bind(this);
	}
	onChange(event) {
		console.log('onChange');
		console.log(event);
		if (event.target.classList.contains('qty')) {
			this.updateQuantity(event.target.dataset.index, event.target.value);
		}
	}
	getSectionsToRender() {
		return [
			{
				id: 'Cart-Drawer',
				section: 'cart-drawer',
				selector: '.cart-drawer',
			},
			{
				id: 'cart-drawer-toggle',
				section: 'cart-bubble',
				selector: '.thb-item-count',
			},
		];
	}
	getSectionInnerHTML(html, selector) {
		return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
	}
	notesToggle() {
		let notes_toggle = document.getElementById('order-note-toggle');

		if (!notes_toggle) {
			return;
		}

		notes_toggle.addEventListener('click', (event) => {
			notes_toggle.nextElementSibling.classList.add('active');
		});
		notes_toggle.nextElementSibling.querySelectorAll('.button, .order-note-toggle__content-overlay').forEach((el) => {
			el.addEventListener('click', (event) => {
				notes_toggle.nextElementSibling.classList.remove('active');
				this.saveNotes();
			});
		});
	}
	saveNotes() {
		fetch(`${theme.routes.cart_update_url}.js`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Accept: `application/json`,
			},
			body: JSON.stringify({
				note: document.getElementById('mini-cart__notes').value,
			}),
		});
	}
	updateQuantity(line, quantity) {
		console.log('updateQuantity');
		console.log(line, quantity);

		const divUpdated = this.container.querySelector(`#CartDrawerItem-${line}`);

		// Cancel any previous quantity update request
		if (this._quantityUpdateController) {
			this._quantityUpdateController.abort();
		}

		// Create a new AbortController for this request
		this._quantityUpdateController = new AbortController();

		divUpdated.classList.add('thb-loading');
		const body = JSON.stringify({
			line,
			quantity,
			sections: this.getSectionsToRender().map((section) => section.section),
			sections_url: window.location.pathname,
		});

		fetch(`${theme.routes.cart_change_url}`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Accept: `application/json`,
			},
			signal: this._quantityUpdateController.signal,
			body,
		})
			.then(async (response) => {
				if (response.status === 200) {
					const state = await response.text();
					this.updateCartUI(state, line);

					if (typeof window.getCartData === 'function') {
						window.getCartData();
					}
				} else {
					const errorMessage = await response.json();
					const restoredQuantity = Number(errorMessage) > 0 ? Number(errorMessage) : quantity - 1;

					divUpdated.querySelector('.error-message').innerHTML = errorMessage.errors;
					divUpdated.querySelector('.qty').value = restoredQuantity;
				}
			})
			.catch((error) => {
				// Ignore aborted requests
				if (error.name !== 'AbortError') {
					console.error('Error updating quantity:', error);
				}
			})
			.finally(() => {
				divUpdated.classList.remove('thb-loading');
			});
	}
	updateCartUI(state, line) {
		const parsedState = JSON.parse(state);

		if (parsedState.sections == null) return;

		// Use CartUtils if available for consistent updates across all cart bubbles
		if (window.CartUtils?.updateSections) {
			window.CartUtils.updateSections(parsedState.sections);
			this.notesToggle();
		} else {
			// Fallback to original behavior
			this.getSectionsToRender().forEach((section) => {
				if (!document.getElementById(section.id)) return;

				const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
				elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);

				let quantities = elementToReplace.querySelectorAll('.quantity:not(.buttons_added)');
				if (quantities) {
					quantities.forEach((quantity) => {
						new QuantityInput(quantity);
					});
				}
				this.notesToggle();
			});
		}
	}
}

/**
 *  @class
 *  @function ModalDialog
 */
class ModalDialog extends HTMLElement {
	constructor() {
		super();
		this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this));
		this.addEventListener('keyup', (event) => {
			if (event.code.toUpperCase() === 'ESCAPE') this.hide();
		});
		if (this.classList.contains('media-modal')) {
			this.addEventListener('pointerup', (event) => {
				if (event.pointerType === 'mouse' && !event.target.closest('product-model')) this.hide();
			});
		} else {
			this.addEventListener('click', (event) => {
				if (event.target.nodeName === 'MODAL-DIALOG') this.hide();
			});
		}
	}

	connectedCallback() {
		if (this.moved) return;
		this.moved = true;
		document.body.appendChild(this);
	}

	show(opener) {
		this.openedBy = opener;
		document.body.classList.add('overflow-hidden');
		this.setAttribute('open', '');
	}

	hide() {
		document.body.classList.remove('overflow-hidden');
		this.removeAttribute('open');
		this.querySelectorAll('.js-youtube').forEach((video) => {
			try {
				if (video.contentWindow) {
					video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
				}
			} catch (err) {
				// Safari can block cross-origin frame access; ignore and let the player handle it.
			}
		});
		this.querySelectorAll('.js-vimeo').forEach((video) => {
			try {
				if (video.contentWindow) {
					video.contentWindow.postMessage('{"method":"pause"}', '*');
				}
			} catch (err) {
				// Safari can block cross-origin frame access; ignore and let the player handle it.
			}
		});
		this.querySelectorAll('video').forEach((video) => video.pause());
	}
}
customElements.define('modal-dialog', ModalDialog);

class ModalOpener extends HTMLElement {
	constructor() {
		super();

		const button = this.querySelector('button');

		if (!button) return;
		button.addEventListener('click', () => {
			const modal = document.querySelector(this.getAttribute('data-modal'));
			if (modal) modal.show(button);
		});
	}
}
customElements.define('modal-opener', ModalOpener);

/**
 *  @class
 *  @function ProductRecommendations
 */
class ProductRecommendations extends HTMLElement {
	constructor() {
		super();

		this.usaInit = false;
	}
	connectedCallback() {
		this.fetchProducts();
		if (window.innerWidth < 1068 && !this.classList.contains('product-recommendations-drawer'))
			this.addEventListener('click', (e) => this.moveSlider(e));
	}
	fetchProducts() {
		fetch(this.dataset.url)
			.then((response) => response.text())
			.then((text) => {
				const html = document.createElement('div');
				html.innerHTML = text;
				const recommendations = html.querySelector('product-recommendations');

				if (recommendations && recommendations.innerHTML.trim().length) {
					this.innerHTML = recommendations.innerHTML;
				}

				let products = this.querySelectorAll('.product-grid');
				products.forEach((product) => {
					new ProductGrid(product);
				});

				this.classList.add('product-recommendations--loaded');
			})
			.catch((e) => {
				console.error(e);
			});
	}
	moveSlider(e) {
		if (e.target.classList.contains('product-recommendations__arrow') || e.target.closest('.product-recommendations__arrow')) {
			const button = e.target.closest('.product-recommendations__arrow');
			const slider = this.querySelector('ul');
			const direction = button.classList.contains('product-recommendations__arrow--next') ? 'left' : 'right';
			const distanceToScroll = slider.querySelector('.product-grid').offsetWidth;

			if (direction === 'left') {
				slider.scrollLeft += distanceToScroll;
			} else {
				slider.scrollLeft -= distanceToScroll;
			}
		}
	}
}

customElements.define('product-recommendations', ProductRecommendations);

/**
 *  @class
 *  @function Localization
 */
class Localization {
	constructor() {
		let _this = this;
		// resize on initial load
		document.querySelectorAll('.thb-localization-forms').forEach((localization) => {
			localization.addEventListener('change', (e) => {
				localization.querySelector('form').submit();
			});
		});
	}
}

/**
 *  @class
 *  @function SelectWidth
 */
class SelectWidth {
	constructor() {
		let _this = this;
		// resize on initial load
		document.querySelectorAll('.resize-select').forEach(_this.resizeSelect);

		// delegated listener on change
		document.body.addEventListener('change', (e) => {
			if (e.target.matches('.resize-select') && e.target.offsetParent !== null) {
				_this.resizeSelect(e.target);
			}
		});
		window.addEventListener('resize.resize-select', function () {
			document.querySelectorAll('.resize-select').forEach(_this.resizeSelect);
		});
	}

	resizeSelect(sel) {
		let tempOption = document.createElement('option');
		tempOption.textContent = sel.selectedOptions[0].textContent;

		let tempSelect = document.createElement('select');
		tempSelect.style.visibility = 'hidden';
		tempSelect.style.position = 'fixed';
		tempSelect.appendChild(tempOption);

		sel.after(tempSelect);
		if (tempSelect.clientWidth > 0) {
			sel.style.width = `${+tempSelect.clientWidth + 10}px`;
		}
		tempSelect.remove();
	}
}
// Nu este in design, dar in caz ca se va vrea
// /**
//  *  @class
//  *  @function ProductQuickSlider
//  */
// class ProductQuickSlider {
//   constructor() {
//     this.container = document.getElementById('Product-Quick-Slider');

//     if (!this.container) {
//       return;
//     }

//     const flkty = new Flickity(this.container, {
//       contain: true,
//       cellAlign: 'left',
//       pageDots: false,
//       freeScroll: true,
//       initialIndex: '.is-initial-selected',
//       prevNextButtons: false,
//       cellSelector: '.product-quick-images__slide'
//     });
//     let scrollbar = document.querySelector('.product-quick-images__scrollbar>div');
//     flkty.on('scroll', function( progress ) {
//       progress = Math.max(0, Math.min(1, progress));
//       scrollbar.style.transform = 'scaleX(' + progress + ')';
//     });

//   }
// }
if (typeof SelectWidth !== 'undefined') {
	new SelectWidth();
}
window.addEventListener('load', () => {
	if (typeof Localization !== 'undefined') {
		new Localization();
	}
	if (typeof CartDrawer !== 'undefined') {
		window.CartDrawer = new CartDrawer();
	}
	if (typeof MobileMenu !== 'undefined') {
		new MobileMenu();
	}
});
